import { DownArrowIcon } from "@xola/icons";
import { Popover, Spinner } from "@xola/ui-kit";
import { ColDef, ColumnState } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import clsx from "clsx";
import { uniqueId } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { Experience, PreferenceContext, PreferenceKey } from "types";
import { SecondaryButton } from "../Common/SecondaryButton";
import { applyFlexToLastVisible } from "./DashboardTable/gridUtils";
import { useDashboardTableStore } from "./DashboardTable/useDashboardTableStore";
import { CreateViewModal } from "./Filters/SavedViewsButton";
import { CustomizeColumnsSection } from "./Roster/RosterGuestList/CustomizeColumns";
import { CustomRosterViewMenuItem } from "./Roster/RosterGuestList/CustomRosterViewMenuItem";
import {
    defaultColumnDefsOrder,
    DefaultColumnDefsOrderInterface,
    getColumnDefs,
} from "./Roster/RosterGuestList/helpers";
import { useQuestionnairePreference, XOLA_DEFAULT_CONFIG_NAME } from "@/hooks/questionnaireFormHooks";
import { useModal } from "@/hooks/useVisibility";
import { useModalStore } from "@/stores/dashboardFilterStore";
import {
    DashboardMode,
    DashboardRosterColumnsConfig,
    DashboardRosterPreference,
    RosterViewConfiguration,
} from "@/types/Dashboard";
import { getSeller } from "@/utils/getSeller";
import { reorder } from "@/utils/reorder";

interface DashboardTableConfigurationProps {
    gridRef: React.RefObject<AgGridReact>;
    visibleExperiences: Experience[];
}

export const normalizePinned = (value: any): "left" | "right" | null => {
    if (value === "left" || value === "right") return value;
    return null;
};

const getTableColumnDefs = (viewConfigurations: RosterViewConfiguration[], visibleExperiences: Experience[]) => {
    return getColumnDefs(viewConfigurations, undefined, visibleExperiences).data;
};

export const DashboardTableConfiguration = ({ gridRef, visibleExperiences }: DashboardTableConfigurationProps) => {
    const { viewConfigurations } = useQuestionnairePreference();

    const selectedView = useDashboardTableStore.getState().selectedTableView;

    const savedViewsPopover = useModal();

    let selectedViewConfiguration = viewConfigurations?.find(
        (viewConfiguration) => viewConfiguration.id === selectedView,
    );

    if (!selectedViewConfiguration) {
        selectedViewConfiguration = viewConfigurations?.find((viewConfiguration) => viewConfiguration.favourite);
    }

    const handleClose = () => {
        if (useModalStore.getState().isOpen && useModalStore.getState().eventId === "Save as New View") {
            return;
        }
        savedViewsPopover.toggle();
    };

    return (
        <div className="flex items-center justify-between">
            <div className="p-2" data-testid="customized-column-saved-views">
                <div
                    className={clsx("flex max-w-30 cursor-pointer whitespace-nowrap font-bold", {
                        "no-translate": !selectedViewConfiguration?.name,
                    })}
                    onClick={savedViewsPopover.toggle}
                >
                    <span className="truncate border-b">{selectedViewConfiguration?.name ?? "Xola Default"}</span>
                    <span>
                        <DownArrowIcon size="small" className="ml-1" />
                    </span>
                </div>
                {savedViewsPopover.isVisible && (
                    <SavedViewPopover
                        viewId={selectedView}
                        gridRef={gridRef}
                        experiences={visibleExperiences}
                        onClose={handleClose}
                    />
                )}
            </div>
            <CustomizeColumnsSection gridRef={gridRef} experience={visibleExperiences} context={DashboardMode.TABLE} />
        </div>
    );
};

const SavedViewPopover = ({
    viewId,
    gridRef,
    experiences,
    onClose,
}: {
    viewId: string | number | null;
    gridRef: React.RefObject<AgGridReact>;
    experiences: Experience[];
    onClose: () => void;
}) => {
    const seller = getSeller();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [isTableRendered, setIsTableRendered] = useState(false);
    const [savingViewId, setSavingViewId] = useState<number | string | null>(null);

    const { preference, viewConfigurations, isLoading, setPreference, savePreference, isSaving } =
        useQuestionnairePreference([], () => setShowCreateModal(false));
    const { setColumnState, setColumnDefs, setIsColumnDefsCustomized, setTableView } = useDashboardTableStore().actions;

    const favoriteViewConfiguration = viewConfigurations.find((viewConfiguration) => viewConfiguration.favourite);
    const [currentTableState, setCurrentTableState] = useState<ColDef[] | null>(null);
    const shouldUpdateColumns = favoriteViewConfiguration && !viewId;

    if (gridRef?.current?.api && !isTableRendered) {
        setIsTableRendered(true);
    }

    const hideDefaultCols = useCallback(
        (viewConfig: RosterViewConfiguration) => {
            // If default config exists, hide no columns
            if (viewConfig.id === XOLA_DEFAULT_CONFIG_NAME) {
                return null;
            }

            // Since we don`t save all columns state, only visible, we should hide columns that we didn`t get from the
            // api
            const columnDefs = getTableColumnDefs(viewConfigurations, experiences);
            const filteredColumnDefs = columnDefs
                .filter((colDef) => !viewConfig.columns.some((newColumn) => newColumn.id === colDef.colId))
                .map((col) => ({ colId: col.colId!, hide: true }));

            gridRef?.current?.api.applyColumnState({
                state: filteredColumnDefs,
            });
        },
        [gridRef, experiences, viewConfigurations],
    );

    const handleItemClick = (viewConfiguration: RosterViewConfiguration) => {
        if (!gridRef.current) return;

        let newColumnOrder: Array<ColumnState>;

        if (viewConfiguration.id === XOLA_DEFAULT_CONFIG_NAME) {
            newColumnOrder = defaultColumnDefsOrder.map((colDef) => ({
                colId: colDef.colId ?? "",
                sort: colDef.sort ?? null,
                hide: colDef.hide ?? true,
                pinned: normalizePinned(colDef.pinned),
            }));
        } else {
            newColumnOrder = viewConfiguration.columns.map((column) => ({
                colId: column.id ?? "",
                sort: column.sort ?? null,
                hide: column.hide ?? false,
                pinned: normalizePinned(column.pinned),
            }));
        }

        // Add flex to the last visible column
        const stateWithFlex = applyFlexToLastVisible(newColumnOrder);

        gridRef.current.api.applyColumnState({
            state: stateWithFlex,
            applyOrder: true,
        });

        const columnDefs = getTableColumnDefs(viewConfigurations, experiences);
        const updatedColumnDefs = columnDefs?.map((col) => {
            const stateCol = stateWithFlex.find((state) => state.colId === col.colId);
            return {
                ...col,
                hide: stateCol ? false : true,
                pinned: stateCol?.pinned ?? null,
                sort: stateCol?.sort ?? null,
                flex: stateCol?.flex ?? undefined,
            };
        });

        setTableView(viewConfiguration.id);
        setIsColumnDefsCustomized(true);
        setColumnState(stateWithFlex);
        hideDefaultCols(viewConfiguration);
        setColumnDefs(updatedColumnDefs);
    };

    const handleItemDelete = (viewConfiguration: RosterViewConfiguration) => {
        const newFavoriteId =
            viewConfiguration.favourite && favoriteViewConfiguration?.id === viewConfiguration.id
                ? XOLA_DEFAULT_CONFIG_NAME
                : favoriteViewConfiguration?.id;

        setPreference((preference: DashboardRosterPreference) => {
            const configurations = preference.value.configurations.filter((configuration) => {
                return configuration.id !== viewConfiguration.id;
            });

            for (const configuration of configurations) {
                configuration.favourite = configuration.id === newFavoriteId;
            }

            preference.value.configurations = configurations;
        });

        const currentView = useDashboardTableStore.getState().selectedTableView;

        if (currentView === viewConfiguration.id) {
            const favoriteView = viewConfigurations.find((view) => view.favourite);

            if (favoriteView && favoriteView.id !== viewConfiguration.id) {
                const columns = favoriteView.columns.map((column) => ({
                    colId: column.id ?? "",
                    sort: column.sort ?? null,
                    hide: column.hide ?? false,
                    pinned: normalizePinned(column.pinned),
                }));

                const stateWithFlex = applyFlexToLastVisible(columns);

                gridRef?.current?.api.applyColumnState({
                    state: stateWithFlex,
                    applyOrder: true,
                });

                setTableView(favoriteView.id);
                return;
            }

            gridRef?.current?.api.applyColumnState({
                state: defaultColumnDefsOrder as ColumnState[],
                applyOrder: true,
            });

            setTableView(XOLA_DEFAULT_CONFIG_NAME);
        }
    };

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        }

        const reOrderedItems = reorder(viewConfigurations, result.source.index, result.destination.index);

        setPreference((preference) => {
            preference.value.configurations = reOrderedItems;
        });
    };

    const handleItemToggleFavourite = (viewConfiguration: RosterViewConfiguration) => {
        // Allows us to toggle the favourite.
        const favouriteId = viewConfiguration.favourite ? null : viewConfiguration.id;

        setPreference((preference: DashboardRosterPreference) => {
            for (const configuration of preference.value.configurations) {
                configuration.favourite = configuration.id === favouriteId;
            }
        });
    };

    const handleSaveItemClick = (viewConfiguration: RosterViewConfiguration) => {
        if (!gridRef?.current) return;
        const columnDefs = gridRef.current?.api.getColumnDefs() as ColDef[];
        const mappedColumnDefs = columnDefs?.map((colDef) => ({
            id: colDef.colId,
            sort: colDef.sort,
            hide: colDef.hide ?? null,
            pinned: colDef.pinned ?? null,
        }));

        const visibleColumns = mappedColumnDefs.filter((column) => !column.hide);

        setPreference((preference) => {
            const updatedConfigurations = preference.value.configurations.map((config) => {
                if (config.id === viewConfiguration.id) {
                    // If found configuration with same id, update columns
                    return {
                        ...config,
                        columns: visibleColumns,
                    };
                }

                return config;
            });

            return {
                ...preference,
                value: {
                    ...preference.value,
                    configurations: updatedConfigurations,
                },
            };
        });

        setSavingViewId(viewConfiguration.id);

        // Need to update current table state after saving preferences to hide selected Preference Save Button
        const updatedColumnDefs = gridRef.current?.api.getColumnDefs() as ColDef[];
        const updatedMappedColumnDefs2 = updatedColumnDefs?.map((colDef) => ({
            colId: colDef.colId,
            sort: colDef.sort ?? null,
            hide: Boolean(colDef.hide) ?? null,
            pinned: colDef.pinned ?? null,
        }));
        setCurrentTableState(updatedMappedColumnDefs2);
    };

    const handleCreateViewConfiguration = async (name: string) => {
        const columnDefs = gridRef.current?.api.getColumnDefs() as ColDef[];
        const mappedColumnDefs = columnDefs?.map((colDef) => ({
            id: colDef.colId,
            sort: colDef.sort,
            hide: colDef.hide ?? false,
            pinned: colDef.pinned ?? null,
        }));
        const visibleColumns = mappedColumnDefs.filter((column) => !column.hide);

        const newId = Date.now();

        const viewConfiguration = {
            id: newId,
            name,
            columns: visibleColumns,
            favourite: false,
        } as any;

        if (preference?.id) {
            // Preference exists, just update it.
            setPreference((preference) => {
                preference.value.configurations.push(viewConfiguration);
            });

            setTableView(newId);
            return;
        }

        // Create new preference.
        savePreference({
            seller: { id: seller.id },
            context: PreferenceContext.USER,
            key: PreferenceKey.XOLA_SELLER_ROSTER,
            value: { configurations: [viewConfiguration] },
        });

        setTableView(newId);
    };

    const applyColumnState = useCallback(
        (columns: Array<ColDef | DashboardRosterColumnsConfig>) => {
            if (!columns) {
                return;
            }
            const newColumnOrder = columns.map((column) => ({
                colId: "colId" in column ? column.colId ?? "" : (column as DashboardRosterColumnsConfig).id || "",
                sort: column.sort,
                hide: column.hide,
                pinned: column.pinned ?? null,
            }));

            gridRef?.current?.api.applyColumnState({
                state: newColumnOrder as ColumnState[],
                applyOrder: true,
            });
        },
        [gridRef],
    );

    useEffect(() => {
        if (savingViewId) {
            const timer = setTimeout(() => {
                setSavingViewId(null);
            }, 1000);

            return () => clearTimeout(timer);
        }
    }, [savingViewId]);

    useEffect(() => {
        if (isTableRendered) {
            const columnDefs = gridRef.current?.api?.getColumnDefs() as DefaultColumnDefsOrderInterface[];
            const mappedColumnDefs = columnDefs?.map((colDef) => ({
                colId: colDef.colId,
                sort: colDef.sort ?? null,
                hide: Boolean(colDef.hide) ?? null,
                pinned: normalizePinned(colDef.pinned),
            }));

            setCurrentTableState(mappedColumnDefs);
        }
    }, [viewId, gridRef, isTableRendered, setCurrentTableState]);

    useEffect(() => {
        if (!shouldUpdateColumns) {
            return;
        }
        const selectedViewConfiguration = useDashboardTableStore.getState().selectedTableView;
        useDashboardTableStore
            .getState()
            .actions.setTableView(selectedViewConfiguration ?? favoriteViewConfiguration?.id);
        setTimeout(() => {
            applyColumnState(favoriteViewConfiguration.columns);
            hideDefaultCols(favoriteViewConfiguration);
        }, 100);
    }, [
        viewId,
        shouldUpdateColumns,
        favoriteViewConfiguration,
        hideDefaultCols,
        applyColumnState,
        experiences,
        viewConfigurations,
    ]);

    return (
        <Popover
            visible
            maxWidth={300}
            offset={[0, 18]}
            zIndex={20}
            placement="bottom"
            trigger="click"
            onClickOutside={onClose}
        >
            <Popover.Content>
                {isLoading ? (
                    <Spinner className="ui-custom-view-menu-loader" />
                ) : (
                    <div className="min-w-[300px]">
                        <h3 className="p-5 pt-3 pb-3">
                            Saved Views{" "}
                            {isSaving ? <Spinner className="ui-custom-view-menu-inline-loader" size="tiny" /> : null}
                        </h3>

                        <div className="flex items-center bg-[#F0F2F4] p-5 pt-2 pb-2">
                            <span className="text-base font-bold">Private Views</span>
                        </div>

                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable
                                droppableId="private_views"
                                renderClone={(provided, _, rubric) => (
                                    <div
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        ref={provided.innerRef}
                                    >
                                        <CustomRosterViewMenuItem
                                            viewConfiguration={viewConfigurations[rubric.source.index]}
                                            currentTableState={currentTableState}
                                            isActive={viewConfigurations[rubric.source.index].id === viewId}
                                            draggableProvided={provided}
                                            isSaving={viewConfigurations[rubric.source.index].id === savingViewId}
                                            isDefaultItem={
                                                viewConfigurations[rubric.source.index].id === XOLA_DEFAULT_CONFIG_NAME
                                            }
                                            onToggleFavorite={handleItemToggleFavourite}
                                            onSaveItemClick={handleSaveItemClick}
                                            onClick={handleItemClick}
                                            onDelete={handleItemDelete}
                                        />
                                    </div>
                                )}
                            >
                                {(provided) => (
                                    <div
                                        ref={provided.innerRef}
                                        className="flex h-full max-h-75 flex-col overflow-auto"
                                        {...provided.droppableProps}
                                    >
                                        {viewConfigurations.map((viewConfiguration, index) => (
                                            <Draggable key={uniqueId("id-")} draggableId={`id-${index}`} index={index}>
                                                {(provided) => (
                                                    <div ref={provided.innerRef} {...provided.draggableProps}>
                                                        <CustomRosterViewMenuItem
                                                            viewConfiguration={viewConfiguration}
                                                            currentTableState={currentTableState}
                                                            isActive={viewConfiguration.id === viewId}
                                                            draggableProvided={provided}
                                                            isSaving={viewConfiguration.id === savingViewId}
                                                            isDefaultItem={
                                                                viewConfiguration.id === XOLA_DEFAULT_CONFIG_NAME
                                                            }
                                                            onToggleFavorite={handleItemToggleFavourite}
                                                            onSaveItemClick={handleSaveItemClick}
                                                            onClick={handleItemClick}
                                                            onDelete={handleItemDelete}
                                                        />
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>

                        <div className="flex items-center bg-[#F0F2F4] p-5 pt-2 pb-2">
                            <span className="text-base font-bold">Shared Views</span>
                        </div>

                        <h3 className="flex justify-center p-5 pt-3 pb-3">Coming Soon!</h3>

                        <div className="space-y-2 border-t  border-t-gray-light p-4">
                            <div>
                                <SecondaryButton className="w-full" onClick={() => setShowCreateModal(true)}>
                                    Save as New View
                                </SecondaryButton>
                            </div>
                        </div>
                    </div>
                )}
            </Popover.Content>
            {showCreateModal && (
                <CreateViewModal
                    isOpen={showCreateModal}
                    isLoading={isSaving}
                    onClose={() => setShowCreateModal(false)}
                    onSubmit={handleCreateViewConfiguration}
                />
            )}
        </Popover>
    );
};
